import Gtag from './components/Gtag';
import MixpanelTracker from './components/MixpanelTracker';
import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useTheme } from './components/ThemeProvider'; // Import your custom hook

// --- Corrected Imports Based on Your Paths ---
import Layout from './components/Layout';
import LoginPage from './pages/auth/Login';
import ProtectedRoute from './components/ProtectedRoute'; // Correct path
import AdminDashboard from './components/AdminDashboard'; // Correct path
import UserDashboard from './components/UserDashboard'; // Correct path
// --- End Corrected Imports ---

import { useAuth } from './lib/auth/AuthContext'; // Assuming this path is correct
import { UserType } from './lib/auth/types'; // Assuming this path is correct

import { useLocation } from 'react-router-dom';

// --- Updated DashboardRedirect ---
function DashboardRedirect() {
  const { userType, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const redirectUri = location.state?.redirectUri;
  if (redirectUri) {
    return <Navigate to={redirectUri} replace />;
  }

  // Allow all authenticated users (including unsubscribed) to access user dashboard
  // The UserDashboard component will handle showing upgrade prompts for unsubscribed users
  if (userType === UserType.UNSUBSCRIBED) {
    return <Navigate to="/user-dashboard" replace />;
  }

  const derivedIsAdmin = userType === UserType.ADMIN;

  return derivedIsAdmin ? <Navigate to="/admin-dashboard" replace /> : <Navigate to="/user-dashboard" replace />;
}
// --- End Updated DashboardRedirect ---

// --- Import Other Page Components ---
import SubscriptionsPage from './pages/SubscriptionsPage';
import CreditsPage from './pages/CreditsPage';
import ReportsPage from './pages/ReportsPage';
import TeamsAdminPage from './pages/TeamsAdminPage';
import TasksPage from './pages/TasksPage';
import TaskDetailPageWrapper from './pages/TaskDetailPage';
import UserManagementPage from './pages/UserManagementPage';
import ViewReportsPage from './pages/ViewReportsPage';
import UnauthorizedPage from './pages/UnauthorizedPage'; // Import UnauthorizedPage
import MonitoringPage from './pages/MonitoringPage';
import NotificationsPage from './pages/NotificationsPage';
import ImageOptimizationPage from './pages/ImageOptimizationPage';
import AgentPage from './pages/AgentPage';
import AgentSettingsPage from './pages/AgentSettingsPage';

// Import TaskQueue components
import { TaskQueueProvider } from './components/tasks/TaskQueueContext';
import TaskQueuePanel from './components/tasks/TaskQueuePanel';
import { DeleteQueueProvider } from './components/tasks/DeleteQueueContext';
import DeleteQueuePanel from './components/tasks/DeleteQueuePanel';

// Import NotificationDeleteQueue components
import { NotificationDeleteQueueProvider } from './components/notifications/NotificationDeleteQueueContext';

import { SidebarProvider } from './context/SidebarContext';
import { TourProvider } from './context/TourContext';
import { MixpanelProvider, useMixpanel } from './context/MixpanelContext';

const ConditionalMixpanelTracker = () => {
  const { isInitialized } = useMixpanel();
  // Only render the tracker if Mixpanel is initialized to prevent race conditions.
  return isInitialized ? <MixpanelTracker /> : null;
};

function App() {
  // Get the current theme (which should be 'light' or 'dark')
  const { theme } = useTheme();

  return (
    <MixpanelProvider>
      <TourProvider>
        <Gtag />
        <ConditionalMixpanelTracker />
        <SidebarProvider>
          <TaskQueueProvider>
            <DeleteQueueProvider>
              <NotificationDeleteQueueProvider>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={<LoginPage />} />

                  {/* Protected Routes */}
                  <Route element={<ProtectedRoute><Layout><Outlet /></Layout></ProtectedRoute>}>
                    <Route path="/" element={<DashboardRedirect />} />
                    <Route path="/admin-dashboard" element={<ProtectedRoute allowedRoles={[UserType.ADMIN]}><AdminDashboard /></ProtectedRoute>} />
                    <Route path="/user-dashboard" element={<ProtectedRoute allowedRoles={[UserType.BASIC, UserType.PRO, UserType.ENTERPRISE, UserType.UNSUBSCRIBED]}><UserDashboard /></ProtectedRoute>} />
                    <Route path="/unauthorized" element={<UnauthorizedPage />} />
                    <Route path="/subscriptions" element={<SubscriptionsPage />} />
                    <Route path="/credits" element={<CreditsPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    {/* Add route for ViewReportPage */}
                    <Route path="/reports/:reportId" element={<ViewReportsPage />} />
                    <Route
                      path="/manage-team"
                      element={
                        <ProtectedRoute allowedRoles={[UserType.ENTERPRISE]}>
                          <TeamsAdminPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/admin/users" element={<ProtectedRoute allowedRoles={[UserType.ADMIN]}><UserManagementPage /></ProtectedRoute>} />
                    <Route path="/tasks" element={<TasksPage />} />
                    <Route path="/tasks/:id" element={<TaskDetailPageWrapper />} />
                    <Route path="/monitoring" element={<MonitoringPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/image-optimization" element={<ImageOptimizationPage />} />
                    <Route path="/agent" element={<AgentPage />} />
                    <Route path="/agent/settings" element={<AgentSettingsPage />} />
                  </Route>

                  {/* Catch-all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

                {/* Global Task Queue Panel - floats above all content */}
                <TaskQueuePanel />

                {/* Global Delete Queue Panel - floats above all content */}
                <DeleteQueuePanel />

                {/* Pass the current theme directly to the Toaster */}
                <Toaster
                  richColors
                  position="top-right"
                  theme={theme} // Use the theme ('light' or 'dark') from your hook
                />
              </NotificationDeleteQueueProvider>
            </DeleteQueueProvider>
          </TaskQueueProvider>
        </SidebarProvider>
      </TourProvider>
    </MixpanelProvider>
  );
}

export default App;